import { useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  Stepper,
  Step,
  StepButton,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
} from "@material-ui/core";
import { useMediaQuery } from "@material-ui/core";
import eyeFill from "@iconify/icons-eva/eye-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import downloadFill from "@iconify/icons-eva/download-fill";
import flashFill from "@iconify/icons-eva/flash-fill";
import Iconify from "src/components/iconify";
import ProposalPreview from "src/components/propuesta/ProposalPreview";
import RegenerarIADialog from "../RegenerarIADialog";
import { useFormik, FormikProvider } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import StepPortada from "./steps/StepPortada";
import StepQuienesSomos from "./steps/StepQuienesSomos";
import StepAntecedentes from "./steps/StepAntecedentes";
import StepEstructura from "./steps/StepEstructura";
import StepFasesServicios from "./steps/StepFasesServicios";
import StepEquipo from "./steps/StepEquipo";
import StepInfoAdicional from "./steps/StepInfoAdicional";
import StepRevision from "./steps/StepRevision";
import TopBarFormularioV2 from "./TopBarFormularioV2";
import { useConfirmDiscard } from "../shared/useConfirmDiscard";
import BannerCambiosSinGuardar from "../shared/BannerCambiosSinGuardar";
import DialogDescartarCambios from "../shared/DialogDescartarCambios";
import { useSelector } from "react-redux";
import { propuestasApi } from "src/services/propuestasApi";
import { useSnackbar } from "src/components/snackbar";
import {
  INTRO_TAXTIC,
  CLAUSULA_CONFIDENCIALIDAD,
} from "./constants/textosFijos";
import {
  buildPropuestaPreview,
  mapItemsFormAFases,
} from "./utils/buildPropuestaPreview";
import { BRAND } from "src/theme/brandTokens";

const PASOS = [
  "Portada", // 0
  "Quiénes Somos", // 1
  "Antecedentes", // 2
  "Estructura", // 3
  "Fases", // 4
  "Equipo", // 5
  "Info. adicional", // 6
  "Revisión", // 7
];

export const ITEM_INITIAL_VALUE = {
  tipoItem: "PLAN",
  refCliente: "",
  nombreCliente: "",
  refEmpleado: "",
  clienteNuevo: null,
  nombreServicio: "",
  tipoMoneda: "CLP",
  valorOriginal: 0,
  descuento: 0,
  valorConDescuento: 0,
  observaciones: "",
  // PLAN
  tipoFrecuencia: "MES",
  fechaSiguienteCiclo: "",
  mesCargaTareas: "",
  mesFacturacion: "",
  seFacturaGestor: true,
  fechaTerminoDesc: "",
  // TAREA
  fechaLimite: "",
  vendidoTaxtic: false,
  // FK blanda al catálogo (refTipoPlan / refTipoTarea, opcionales) + campos
  // narrativos copiados desde el catálogo y editables por el usuario.
  refTipoPlan: "",
  refTipoTarea: "",
  faseTitulo: "",
  faseObjetivo: "",
  faseActividades: "",
  faseEntregable: "",
  faseDuracion: "",
};

const FormularioPropuestaV2 = ({
  onVolverAlGate,
  borradorInicialIA,
  promptOriginalInicial,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { empleado: authEmpleado, sucursal: authSucursal } = useSelector(
    (state) => state.auth.user,
  );

  const [pasoActivo, setPasoActivo] = useState(0);
  const [camposIA, setCamposIA] = useState(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [regenerarOpen, setRegenerarOpen] = useState(false);
  // Metadata de razon_seleccion poblada por aplicarBorrador cuando fuenteIA=true.
  // Shape: { [itemIndex]: string } — vive solo en state local, nunca va al backend.
  // Se limpia cuando el vendor guarda cambios en un item desde FaseEditDialog.
  const [razonesSeleccionIA, setRazonesSeleccionIA] = useState({});
  const isSmall = useMediaQuery("(max-width:1279px)");
  const isXL = useMediaQuery("(min-width:1600px)");

  const [addPropuestaV2, { isLoading: isGuardando }] =
    propuestasApi.useAddPropuestaV2Mutation();

  const [previewPDFMutation, { isLoading: descargandoBorrador }] =
    propuestasApi.usePreviewPDFPropuestaV2Mutation();

  const [regenerarConIA, { isLoading: isRegenerando }] =
    propuestasApi.useGenerarConIAMutation();

  const descargarBorradorPDF = async () => {
    const { values } = formik;
    const solicitante = values.solicitanteData || values.solicitanteNuevo;

    const payload = {
      titulo: values.titulo,
      descripcion: values.descripcion,
      antecedentes: values.antecedentes,
      alcance: values.alcance,
      resumenEjecutivo: values.resumenEjecutivo,
      condicionesPago: values.condicionesPago,
      duracionEstimada: values.duracionEstimada,
      proximosPasos: values.proximosPasos,
      introduccion: values.introduccion,
      clausulaConfidencialidad: values.clausulaConfidencialidad,
      vendedor: values.vendedor,
      vendedorCorreo: values.vendedorCorreo,
      sucursal: values.sucursal,
      solicitante: solicitante
        ? {
            nombre: solicitante.nombre,
            correo: solicitante.correo,
            telefono: solicitante.telefono,
          }
        : null,
      items: values.items,
      equipo: values.equipo,
    };

    try {
      const blob = await previewPDFMutation(payload).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "borrador-propuesta.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar("Error al generar el PDF.", { variant: "error" });
    }
  };

  const marcarCamposIA = (campos) => {
    setCamposIA((prev) => {
      const next = new Set(prev);
      campos.forEach((c) => next.add(c));
      return next;
    });
  };

  const desmarcarCampoIA = (campo) => {
    setCamposIA((prev) => {
      const next = new Set(prev);
      next.delete(campo);
      return next;
    });
  };

  const limpiarRazonSeleccionIA = (indice) => {
    setRazonesSeleccionIA((prev) => {
      if (!(indice in prev)) return prev;
      const next = { ...prev };
      delete next[indice];
      return next;
    });
  };

  const resetRazonesSeleccionIA = () => {
    setRazonesSeleccionIA({});
  };

  const swapRazonesSeleccionIA = (i, j) => {
    setRazonesSeleccionIA((prev) => {
      const a = prev[i];
      const b = prev[j];
      if (a === undefined && b === undefined) return prev;
      const next = { ...prev };
      if (a !== undefined) next[j] = a;
      else delete next[j];
      if (b !== undefined) next[i] = b;
      else delete next[i];
      return next;
    });
  };

  const formik = useFormik({
    initialValues: {
      // Paso 1 — Portada
      titulo: "",
      descripcion: "",
      solicitanteId: null,
      solicitanteNuevo: null,
      solicitanteData: null, // datos completos del solicitante existente
      sucursal: authSucursal?.refSucursal || "",
      // Paso 2 — Quiénes Somos
      introduccion: INTRO_TAXTIC,
      resumenEjecutivo: "",
      // Paso 3 — Antecedentes
      antecedentes: "",
      // Paso 4 — Estructura
      alcance: "",
      // Paso 5 — Fases
      items: [],
      // Paso 6 — Equipo
      vendedor: authEmpleado?.rut || "",
      vendedorCorreo: authEmpleado?.correo || "",
      vendedorTelefono: "",
      supervisor: "",
      equipo: [],
      // Paso 7 — Info. adicional
      condicionesPago: "",
      duracionEstimada: "",
      proximosPasos: "",
      clausulaConfidencialidad: CLAUSULA_CONFIDENCIALIDAD,
      // Meta
      generadoConIA: false,
      promptOriginal: "",
      // Migración V1 -> V2 (solo presente si el formulario fue invocado desde el botón "Migrar a V2" del listado unificado)
      origenMigracion: null,
    },
    onSubmit: async (values) => {
      if (!values.vendedor) {
        enqueueSnackbar("Selecciona un vendedor.", { variant: "warning" });
        return;
      }
      if (!values.sucursal) {
        enqueueSnackbar("Selecciona una sucursal.", { variant: "warning" });
        return;
      }

      const payload = {
        vendedor: values.vendedor,
        sucursal: values.sucursal,
        ...(values.supervisor ? { supervisor: values.supervisor } : {}),
        ...(values.solicitanteId
          ? { solicitanteId: values.solicitanteId }
          : values.solicitanteNuevo
            ? { solicitante: values.solicitanteNuevo }
            : {}),
        ...(values.titulo ? { titulo: values.titulo } : {}),
        ...(values.descripcion ? { descripcion: values.descripcion } : {}),
        ...(values.antecedentes ? { antecedentes: values.antecedentes } : {}),
        ...(values.resumenEjecutivo
          ? { resumenEjecutivo: values.resumenEjecutivo }
          : {}),
        ...(values.alcance ? { alcance: values.alcance } : {}),
        ...(values.condicionesPago
          ? { condicionesPago: values.condicionesPago }
          : {}),
        ...(values.duracionEstimada
          ? { duracionEstimada: values.duracionEstimada }
          : {}),
        ...(values.proximosPasos
          ? { proximosPasos: values.proximosPasos }
          : {}),
        ...(values.introduccion ? { introduccion: values.introduccion } : {}),
        ...(values.clausulaConfidencialidad
          ? { clausulaConfidencialidad: values.clausulaConfidencialidad }
          : {}),
        generadoConIA: values.generadoConIA,
        ...(values.promptOriginal
          ? { promptOriginal: values.promptOriginal }
          : {}),
        items: values.items.map((item) => ({
          tipoItem: item.tipoItem,
          refCliente: item.refCliente,
          refEmpleado: item.refEmpleado,
          nombreServicio: item.nombreServicio,
          tipoMoneda: item.tipoMoneda,
          valorOriginal: Number(item.valorOriginal),
          descuento: Number(item.descuento),
          valorConDescuento: Number(item.valorConDescuento),
          ...(item.observaciones ? { observaciones: item.observaciones } : {}),
          // FK al catálogo y campos narrativos (todos opcionales).
          ...(item.refTipoPlan ? { refTipoPlan: item.refTipoPlan } : {}),
          ...(item.refTipoTarea ? { refTipoTarea: item.refTipoTarea } : {}),
          ...(item.faseTitulo ? { faseTitulo: item.faseTitulo } : {}),
          ...(item.faseObjetivo ? { faseObjetivo: item.faseObjetivo } : {}),
          ...(item.faseActividades
            ? { faseActividades: item.faseActividades }
            : {}),
          ...(item.faseEntregable
            ? { faseEntregable: item.faseEntregable }
            : {}),
          ...(item.faseDuracion ? { faseDuracion: item.faseDuracion } : {}),
          ...(item.tipoItem === "PLAN"
            ? {
                tipoFrecuencia: item.tipoFrecuencia,
                ...(item.fechaSiguienteCiclo
                  ? { fechaSiguienteCiclo: item.fechaSiguienteCiclo }
                  : {}),
                ...(item.mesCargaTareas
                  ? { mesCargaTareas: item.mesCargaTareas }
                  : {}),
                ...(item.mesFacturacion
                  ? { mesFacturacion: item.mesFacturacion }
                  : {}),
                seFacturaGestor: item.seFacturaGestor,
                ...(item.fechaTerminoDesc
                  ? { fechaTerminoDesc: item.fechaTerminoDesc }
                  : {}),
              }
            : {}),
          ...(item.tipoItem === "TAREA"
            ? {
                ...(item.fechaLimite ? { fechaLimite: item.fechaLimite } : {}),
                vendidoTaxtic: item.vendidoTaxtic,
                ...(item.fechaTerminoDesc
                  ? { fechaTerminoDesc: item.fechaTerminoDesc }
                  : {}),
              }
            : {}),
        })),
        equipo: values.equipo.map((m, i) => ({
          orden: i,
          rolDisplay: m.rolDisplay,
          nombre: m.nombre,
          ...(m.fotoUrl ? { fotoUrl: m.fotoUrl } : {}),
          ...(m.descripcionExperiencia
            ? { descripcionExperiencia: m.descripcionExperiencia }
            : {}),
        })),
      };

      try {
        const resultado = await addPropuestaV2(payload).unwrap();
        enqueueSnackbar("Propuesta creada exitosamente.", {
          variant: "success",
        });
        navigate(`/dashboard/propuestas/v2/${resultado.idPropuesta}`);
      } catch {
        enqueueSnackbar("Error al guardar la propuesta. Intenta nuevamente.", {
          variant: "error",
        });
      }
    },
  });

  const { requestLeave, dialogOpen, onSeguirEditando, onDescartar } =
    useConfirmDiscard({ dirty: formik.dirty });

  /**
   * Aplica el borrador generado por la IA al formulario.
   * Convierte las fases de IA en items con campos narrativos.
   */
  const aplicarBorrador = (borrador, { fuenteIA = true } = {}) => {
    if (borrador) {
      const camposTexto = [
        "titulo",
        "descripcion",
        "antecedentes",
        "alcance",
        "condicionesPago",
        "proximosPasos",
      ];
      camposTexto.forEach((c) => {
        if (borrador[c]) formik.setFieldValue(c, borrador[c]);
      });

      if (borrador.fases && borrador.fases.length > 0) {
        // Tabla de normalización: valores del LLM -> valores del selector del formulario.
        // FaseEditDialog acepta ["CLP", "UF", "USD", "EUR"]. El LLM emite
        // ["PESOS", "UF", "DOLAR", "EURO", "UTM"]. UF coincide directamente;
        // los demás se mapean aquí para que formik siempre tenga valores consistentes.
        const MONEDA_LLM_A_FORM = {
          PESOS: "CLP",
          DOLAR: "USD",
          EURO: "EUR",
          // UF y CLP pasan directo
        };
        const MONEDAS_VALIDAS_FORM = ["CLP", "UF", "USD", "EUR"];

        const normalizarMonedaLLM = (m) => {
          if (!m) return ITEM_INITIAL_VALUE.tipoMoneda;
          const upper = String(m).toUpperCase().trim();
          if (MONEDA_LLM_A_FORM[upper]) return MONEDA_LLM_A_FORM[upper];
          if (MONEDAS_VALIDAS_FORM.includes(upper)) return upper;
          console.warn(
            `[PropuestasV2] tipo_moneda "${m}" no tiene equivalente en el selector del form V2 — usando ${ITEM_INITIAL_VALUE.tipoMoneda} por defecto.`,
          );
          return ITEM_INITIAL_VALUE.tipoMoneda;
        };

        const itemsDesdeIA = borrador.fases.map((fase) => {
          const { razon_seleccion, tipo_moneda, ...restFase } = fase;
          // `nombreServicio` es el identificador interno (vínculo al catálogo).
          // Preferir el nombre técnico del catálogo (`ref_tipo_plan`/
          // `ref_tipo_tarea`); cae al `titulo` amigable del LLM solo cuando la
          // fase es TAREA sin match en el catálogo (servicio libre).
          // `faseTitulo` siempre amigable — es lo que el cliente ve en el PDF.
          const refCatalogo =
            restFase.ref_tipo_plan || restFase.ref_tipo_tarea || "";
          return {
            ...ITEM_INITIAL_VALUE,
            tipoItem: restFase.tipo_item === "TAREA" ? "TAREA" : "PLAN",
            nombreServicio: refCatalogo || restFase.titulo || "",
            faseTitulo: restFase.titulo || "",
            faseObjetivo: restFase.objetivo || "",
            faseActividades: restFase.actividades || "",
            faseEntregable: restFase.entregable || "",
            faseDuracion: restFase.duracion || "",
            refTipoPlan: restFase.ref_tipo_plan || "",
            refTipoTarea: restFase.ref_tipo_tarea || "",
            tipoMoneda: normalizarMonedaLLM(tipo_moneda),
          };
        });
        formik.setFieldValue("items", itemsDesdeIA);

        // Capturar razon_seleccion por item cuando la fuente es IA.
        if (fuenteIA) {
          const razones = borrador.fases.reduce((acc, fase, i) => {
            if (fase.razon_seleccion) acc[i] = fase.razon_seleccion;
            return acc;
          }, {});
          setRazonesSeleccionIA(razones);
        } else {
          setRazonesSeleccionIA({});
        }
      } else {
        setRazonesSeleccionIA({});
      }

      if (fuenteIA) {
        formik.setFieldValue("generadoConIA", true);
        marcarCamposIA(camposTexto.filter((c) => !!borrador[c]));
      }
    }
    setPasoActivo(0);
  };

  const handleRegenerar = async (nuevoPrompt) => {
    try {
      const resultado = await regenerarConIA({
        idea: nuevoPrompt,
        ...(formik.values.sucursal ? { sucursal: formik.values.sucursal } : {}),
      }).unwrap();

      borradorInicialAplicadoRef.current = false;
      aplicarBorrador(resultado.borrador, { fuenteIA: true });
      formik.setFieldValue("promptOriginal", nuevoPrompt);
      setRegenerarOpen(false);
      enqueueSnackbar("Propuesta regenerada con IA", { variant: "success" });
    } catch {
      enqueueSnackbar(
        "No se pudo regenerar la propuesta. Intenta nuevamente.",
        { variant: "error" },
      );
    }
  };

  // Bloquea el paso Portada cuando la migración viene desde una propuesta V1 TAREA
  // sin solicitante_id: el vendor debe seleccionar o crear uno antes de avanzar.
  const portadaBloqueadaPorMigracion =
    formik.values.origenMigracion?.tipo === "TAREA" &&
    formik.values.origenMigracion?.solicitanteId == null &&
    !formik.values.solicitanteId &&
    !formik.values.solicitanteNuevo;

  // ── Aplicar borrador de migración V1 -> V2 si la navegación vino con ese state
  const borradorAplicadoRef = useRef(false);
  useEffect(() => {
    if (borradorAplicadoRef.current) return;
    const borradorMigracion = location.state?.borradorMigracion;
    const origenMigracion = location.state?.origenMigracion;
    if (!borradorMigracion) return;

    borradorAplicadoRef.current = true;
    aplicarBorrador(borradorMigracion, { fuenteIA: false });
    if (origenMigracion) {
      formik.setFieldValue("origenMigracion", origenMigracion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Aplica el borrador IA y/o el promptOriginal recibidos como props desde
  // NuevaPropuestaV2 cuando el vendor pasa por el gate. Idempotente: el ref
  // garantiza una sola aplicación aunque las props cambien de identidad.
  const borradorInicialAplicadoRef = useRef(false);
  useEffect(() => {
    if (borradorInicialAplicadoRef.current) return;
    if (!borradorInicialIA && !promptOriginalInicial) return;
    borradorInicialAplicadoRef.current = true;
    if (borradorInicialIA) {
      aplicarBorrador(borradorInicialIA, { fuenteIA: true });
    }
    if (promptOriginalInicial) {
      formik.setFieldValue("promptOriginal", promptOriginalInicial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borradorInicialIA, promptOriginalInicial]);

  const renderPaso = () => {
    switch (pasoActivo) {
      case 0:
        return (
          <StepPortada
            formik={formik}
            camposIA={camposIA}
            desmarcarCampoIA={desmarcarCampoIA}
          />
        );
      case 1:
        return <StepQuienesSomos formik={formik} />;
      case 2:
        return (
          <StepAntecedentes
            formik={formik}
            camposIA={camposIA}
            desmarcarCampoIA={desmarcarCampoIA}
          />
        );
      case 3:
        return (
          <StepEstructura
            formik={formik}
            camposIA={camposIA}
            desmarcarCampoIA={desmarcarCampoIA}
          />
        );
      case 4:
        return (
          <StepFasesServicios
            formik={formik}
            razonesSeleccionIA={razonesSeleccionIA}
            limpiarRazonSeleccionIA={limpiarRazonSeleccionIA}
            resetRazonesSeleccionIA={resetRazonesSeleccionIA}
            swapRazonesSeleccionIA={swapRazonesSeleccionIA}
          />
        );
      case 5:
        return <StepEquipo formik={formik} />;
      case 6:
        return <StepInfoAdicional formik={formik} />;
      case 7:
        return <StepRevision formik={formik} isGuardando={isGuardando} />;
      default:
        return null;
    }
  };

  return (
    <FormikProvider value={formik}>
      <TopBarFormularioV2
        titulo="Nueva propuesta"
        onGuardarBorrador={formik.submitForm}
        guardando={formik.isSubmitting || isGuardando}
        disabled={!formik.values.vendedor || !formik.values.sucursal}
      />
      <BannerCambiosSinGuardar
        visible={formik.dirty}
        mensaje={
          <>
            Tienes cambios sin guardar. No olvides presionar{" "}
            <strong>Guardar borrador</strong> antes de salir.
          </>
        }
      />
      <Box sx={{ display: "flex", minHeight: "80vh" }}>
        {/* Panel izquierdo — Stepper vertical */}
        {!isSmall && (
          <Box
            sx={{
              width: 200,
              flexShrink: 0,
              borderRight: "1px solid",
              borderColor: "divider",
              pt: 3,
              pb: 3,
              pl: 2,
              pr: 1,
            }}
          >
            <Stepper activeStep={pasoActivo} orientation="vertical" nonLinear>
              {PASOS.map((label, index) => (
                <Step key={label} completed={index < pasoActivo}>
                  <StepButton
                    onClick={() => setPasoActivo(index)}
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontWeight: pasoActivo === index ? 700 : 400,
                        color:
                          pasoActivo === index
                            ? BRAND.color.primary
                            : "text.secondary",
                      },
                      "& .MuiStepIcon-root.Mui-active": {
                        color: BRAND.color.primary,
                      },
                      "& .MuiStepIcon-root.Mui-completed": {
                        color: BRAND.color.primary,
                      },
                    }}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        {/* Panel central — Contenido del paso */}
        <Box sx={{ flex: 1, p: 3, overflowY: "auto", minWidth: 0 }}>
          {/* Barra superior */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            {isSmall ? (
              <Typography variant="caption" color="textSecondary">
                Paso {pasoActivo + 1} de {PASOS.length} —{" "}
                <strong>{PASOS[pasoActivo]}</strong>
              </Typography>
            ) : formik.values.generadoConIA ? (
              <Chip
                size="small"
                icon={<Iconify icon={flashFill} width={14} />}
                label="Generado con IA · ver prompt"
                onClick={() => setRegenerarOpen(true)}
                sx={{
                  bgcolor: BRAND.color.primaryBg,
                  color: BRAND.color.primary,
                  fontWeight: 600,
                  fontSize: 11,
                  border: `1px solid ${BRAND.color.primaryHoverBg}`,
                  cursor: "pointer",
                  "&:hover": { bgcolor: BRAND.color.primaryHoverBg },
                }}
              />
            ) : (
              <Box />
            )}
            {!isXL && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Iconify icon={eyeFill} width={16} height={16} />}
                  onClick={() => setPreviewOpen(true)}
                  sx={{
                    borderColor: "divider",
                    color: "text.secondary",
                    flexShrink: 0,
                  }}
                >
                  Vista previa
                </Button>
                {pasoActivo === 7 && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      descargandoBorrador ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <Iconify icon={downloadFill} width={16} height={16} />
                      )
                    }
                    disabled={descargandoBorrador}
                    onClick={descargarBorradorPDF}
                    sx={{
                      borderColor: BRAND.color.primary,
                      color: BRAND.color.primary,
                      flexShrink: 0,
                    }}
                  >
                    {descargandoBorrador ? "Generando..." : "Descargar PDF"}
                  </Button>
                )}
              </Stack>
            )}
          </Box>

          {renderPaso()}

          {/* Navegación pasos 0–6 (Portada -> Info. adicional). Paso 7
              (Revisión) tiene su propio botón "Volver" más abajo. */}
          {pasoActivo <= 6 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 4,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              {pasoActivo > 0 ? (
                <Button
                  variant="outlined"
                  onClick={() => setPasoActivo((p) => p - 1)}
                >
                  Anterior
                </Button>
              ) : (
                <Box />
              )}
              <Button
                variant="contained"
                onClick={() => setPasoActivo((p) => p + 1)}
                disabled={pasoActivo === 0 && portadaBloqueadaPorMigracion}
                sx={{
                  bgcolor: BRAND.color.primary,
                  "&:hover": { bgcolor: BRAND.color.primaryDark },
                }}
              >
                Siguiente
              </Button>
            </Box>
          )}

          {/* Volver desde revisión */}
          {pasoActivo === 7 && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                color="inherit"
                onClick={() => setPasoActivo(6)}
              >
                Volver
              </Button>
            </Box>
          )}

          {/* Volver al gate desde el paso inicial */}
          {pasoActivo === 0 && onVolverAlGate && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="text"
                color="inherit"
                onClick={() => requestLeave(onVolverAlGate)}
              >
                Volver al inicio
              </Button>
            </Box>
          )}
        </Box>

        {/* Panel de preview lateral — visible en pantallas xl */}
        {isXL && (
          <Box
            sx={{
              width: 380,
              flexShrink: 0,
              borderLeft: "1px solid",
              borderColor: "divider",
              overflowY: "auto",
              p: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Iconify icon={eyeFill} width={16} height={16} />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontWeight: 600 }}
              >
                Vista previa (en vivo)
              </Typography>
            </Box>
            <ProposalPreview
              propuesta={buildPropuestaPreview(formik.values)}
              fases={mapItemsFormAFases(formik.values.items)}
              equipo={formik.values.equipo}
            />
          </Box>
        )}

        {/* Dialog vista previa — solo en pantallas menores a xl */}
        {!isXL && (
          <Dialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            fullWidth
            maxWidth="md"
            scroll="paper"
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: "1px solid",
                borderColor: "divider",
                pb: 1.5,
              }}
            >
              <Typography variant="h6">Vista previa de la propuesta</Typography>
              <IconButton size="small" onClick={() => setPreviewOpen(false)}>
                <Iconify icon={closeFill} width={20} height={20} />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 2 }}>
              <ProposalPreview
                propuesta={buildPropuestaPreview(formik.values)}
                fases={mapItemsFormAFases(formik.values.items)}
                equipo={formik.values.equipo}
              />
            </DialogContent>
          </Dialog>
        )}
      </Box>
      <RegenerarIADialog
        open={regenerarOpen}
        onClose={() => setRegenerarOpen(false)}
        promptOriginal={formik.values.promptOriginal || ""}
        onRegenerar={handleRegenerar}
        isLoading={isRegenerando}
      />
      <DialogDescartarCambios
        open={dialogOpen}
        onSeguir={onSeguirEditando}
        onDescartar={onDescartar}
      />
    </FormikProvider>
  );
};

FormularioPropuestaV2.propTypes = {
  onVolverAlGate: PropTypes.func,
  borradorInicialIA: PropTypes.object,
  promptOriginalInicial: PropTypes.string,
};

FormularioPropuestaV2.defaultProps = {
  onVolverAlGate: undefined,
  borradorInicialIA: undefined,
  promptOriginalInicial: "",
};

export default FormularioPropuestaV2;
